import React from 'react';
import { cn } from '../../../lib/cn';

/**
 * Field family, the desktop form scaffolding.
 *
 * Source selectors: `system.css` `.dsk-field`, `.dsk-field--full`,
 * `.dsk-field__foot`, `.dsk-fields`, `.dsk-fields--1`, `.dsk-fields--3`,
 * `.dsk-label`, `.dsk-label__opt`, `.dsk-hint`, `.dsk-error`.
 *
 * Doctrine enforced:
 * - Required is the norm on Sleevr forms, so the OPTIONAL case is the one that
 *   gets marked (`.dsk-label__opt`), never a star on most of the form.
 * - Error is text plus border, never a filled red surface. `Field` only sets
 *   `aria-invalid`, the red comes from `.dsk-input--error` on the control.
 * - `.dsk-fields` is `repeat(N, minmax(0, 1fr))` upstream, never `1fr`, so a
 *   long value cannot push a track past its column.
 *
 * Label association is real, not visual. `Field` mints one id with `useId()`
 * and hands it down through `FieldContext`; every control in this directory
 * calls `useFieldControl()` and picks up `id`, `aria-describedby` (hint and
 * error) and `aria-invalid` without the caller wiring anything.
 *
 *   <Field label="Asking price" hint="Rands, excluding shipping" error={err}>
 *     <Input inputMode="decimal" />
 *   </Field>
 */
export const FieldContext = React.createContext(null);

/**
 * Merges the surrounding `Field`'s identity into a control's own props.
 * A control's explicit `id` / `aria-describedby` always wins over the field's.
 */
export function useFieldControl(props = {}) {
  const field = React.useContext(FieldContext);
  const {
    id,
    'aria-describedby': describedBy,
    'aria-invalid': invalid,
    'aria-labelledby': labelledBy,
  } = props;

  if (!field) {
    return {
      id,
      'aria-describedby': describedBy || undefined,
      'aria-invalid': invalid,
      'aria-labelledby': labelledBy,
      labelId: undefined,
    };
  }

  const described = [describedBy, field.describedBy].filter(Boolean).join(' ');

  return {
    id: id || field.controlId,
    'aria-describedby': described || undefined,
    'aria-invalid': invalid != null ? invalid : field.invalid || undefined,
    'aria-labelledby': labelledBy,
    labelId: field.labelId,
  };
}

/**
 * Fields, the field grid. `.dsk-fields` (2 up), `--1`, `--3`.
 * Two columns is the un-modified base rule, so that is the default.
 */
export const Fields = React.forwardRef(function Fields(
  { cols = 2, className, ...rest },
  ref,
) {
  const colsClass = { 1: 'dsk-fields--1', 2: null, 3: 'dsk-fields--3' }[cols];
  return <div ref={ref} className={cn('dsk-fields', colsClass, className)} {...rest} />;
});
Fields.displayName = 'Fields';

/**
 * Label, `.dsk-label` plus the `.dsk-label__opt` optional marker.
 * Rendered by `Field` for you; exported for hand-built layouts.
 */
export const Label = React.forwardRef(function Label(
  { optional = false, optionalText = 'Optional', children, className, ...rest },
  ref,
) {
  return (
    <label ref={ref} className={cn('dsk-label', className)} {...rest}>
      {children}
      {optional ? <span className="dsk-label__opt"> {optionalText}</span> : null}
    </label>
  );
});
Label.displayName = 'Label';

/** Hint, `.dsk-hint`. The quiet line under a control. */
export const Hint = React.forwardRef(function Hint({ className, ...rest }, ref) {
  return <span ref={ref} className={cn('dsk-hint', className)} {...rest} />;
});
Hint.displayName = 'Hint';

/**
 * ErrorText, `.dsk-error`. Text and colour only, never a filled surface.
 * `.dsk-error[hidden]` is handled upstream, so `hidden` works as expected.
 */
export const ErrorText = React.forwardRef(function ErrorText({ className, ...rest }, ref) {
  return <span ref={ref} className={cn('dsk-error', className)} role="alert" {...rest} />;
});
ErrorText.displayName = 'ErrorText';

/** FieldFoot, `.dsk-field__foot`. A row of hint plus counter under a control. */
export const FieldFoot = React.forwardRef(function FieldFoot({ className, ...rest }, ref) {
  return <div ref={ref} className={cn('dsk-field__foot', className)} {...rest} />;
});
FieldFoot.displayName = 'FieldFoot';

/**
 * Field, `.dsk-field` (+ `.dsk-field--full` to span a `Fields` grid).
 * Order is label, control, hint, error, which is the order the eye needs them.
 */
export const Field = React.forwardRef(function Field(
  {
    label,
    hint,
    error,
    optional = false,
    optionalText = 'Optional',
    full = false,
    htmlFor,
    id,
    children,
    className,
    ...rest
  },
  ref,
) {
  const uid = React.useId();
  const controlId = htmlFor || id || `${uid}-control`;
  const labelId = `${uid}-label`;
  const hintId = `${uid}-hint`;
  const errorId = `${uid}-error`;

  const describedBy = [hint ? hintId : null, error ? errorId : null]
    .filter(Boolean)
    .join(' ');

  const ctx = React.useMemo(
    () => ({
      controlId,
      labelId,
      describedBy: describedBy || undefined,
      invalid: error ? true : undefined,
    }),
    [controlId, labelId, describedBy, error],
  );

  return (
    <div ref={ref} className={cn('dsk-field', full && 'dsk-field--full', className)} {...rest}>
      {label ? (
        <Label id={labelId} htmlFor={controlId} optional={optional} optionalText={optionalText}>
          {label}
        </Label>
      ) : null}
      <FieldContext.Provider value={ctx}>{children}</FieldContext.Provider>
      {hint ? <Hint id={hintId}>{hint}</Hint> : null}
      {error ? <ErrorText id={errorId}>{error}</ErrorText> : null}
    </div>
  );
});
Field.displayName = 'Field';

export default Field;
